import React, { useState } from 'react';
import { ProcessArtefakt } from '../types/process';
import './ProcessList.css';

interface ProcessListProps {
  artefakte: ProcessArtefakt[];
  onView: (artefakt: ProcessArtefakt) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, newTitel: string) => void;
  onDuplicate: (id: string) => void;
  onCombine?: (selectedIds: string[]) => void;
  onBack: () => void;
}

const ProcessList: React.FC<ProcessListProps> = ({
  artefakte,
  onView,
  onDelete,
  onRename,
  onDuplicate,
  onCombine,
  onBack
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleStartEdit = (artefakt: ProcessArtefakt) => {
    setEditingId(artefakt.id);
    setEditValue(artefakt.titel);
  };

  const handleSaveEdit = (id: string) => {
    if (editValue.trim()) {
      onRename(id, editValue.trim());
    }
    setEditingId(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDeleteWithConfirm = (id: string, titel: string) => {
    if (window.confirm(`Möchten Sie "${titel}" wirklich löschen?`)) {
      onDelete(id);
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCombineClick = () => {
    if (onCombine && selectedIds.length >= 2) {
      onCombine(selectedIds);
      setSelectedIds([]);
    }
  };

  return (
    <div className="process-list">
      <div className="process-list-header">
        <button className="back-button" onClick={onBack}>
          ← Zurück
        </button>
        <h2>Gespeicherte Prozesse</h2>
        <div className="count-badge">{artefakte.length} Prozess(e)</div>
        {onCombine && selectedIds.length >= 2 && (
          <button
            className="btn-combine"
            onClick={handleCombineClick}
            style={{
              marginLeft: 'auto',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(139, 92, 246, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            🔗 {selectedIds.length} Prozesse kombinieren
          </button>
        )}
      </div>

      {artefakte.length === 0 ? (
        <div className="empty-state">
          <p>Keine gespeicherten Prozesse vorhanden.</p>
          <p className="empty-subtitle">
            Erstellen Sie einen neuen Prozess, um ihn hier zu sehen.
          </p>
        </div>
      ) : (
        <div className="process-grid">
          {artefakte.map(artefakt => (
            <div key={artefakt.id} className="process-card" style={{ position: 'relative' }}>
              {onCombine && (
                <div style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(artefakt.id)}
                    onChange={() => handleToggleSelect(artefakt.id)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: 'var(--accent-primary)'
                    }}
                  />
                </div>
              )}
              {editingId === artefakt.id ? (
                <div className="edit-mode">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(artefakt.id);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    autoFocus
                  />
                  <div className="edit-actions">
                    <button
                      className="btn-save"
                      onClick={() => handleSaveEdit(artefakt.id)}
                    >
                      ✓
                    </button>
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      ✕
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="card-header">
                    <h3>{artefakt.titel}</h3>
                    <div className="card-meta">
                      {new Date(artefakt.erstelltAm).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>

                  <div className="card-preview">
                    <div className="preview-row">
                      <strong>Rollen:</strong> {artefakt.beschreibung.rollen.length}
                    </div>
                    <div className="preview-row">
                      <strong>Prozesse:</strong> {artefakt.beschreibung.prozesse.length}
                    </div>
                    {artefakt.hinweise.length > 0 && (
                      <div className="preview-row">
                        <strong>Hinweise:</strong> {artefakt.hinweise.length}
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn-action btn-view"
                      onClick={() => onView(artefakt)}
                      title="Anzeigen"
                    >
                      👁 Anzeigen
                    </button>
                    <button
                      className="btn-action btn-edit"
                      onClick={() => handleStartEdit(artefakt)}
                      title="Umbenennen"
                    >
                      ✏ Umbenennen
                    </button>
                    <button
                      className="btn-action btn-duplicate"
                      onClick={() => onDuplicate(artefakt.id)}
                      title="Duplizieren"
                    >
                      📋 Duplizieren
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() =>
                        handleDeleteWithConfirm(artefakt.id, artefakt.titel)
                      }
                      title="Löschen"
                    >
                      🗑 Löschen
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProcessList;
